import React, { useState } from 'react';
import { connect } from 'react-redux';

import { getItemStatusClassName } from 'helpers/MyAccountRenderUtils';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import {
  CANCELLED,
  COMPLETED,
  CUSTOMER_ACTION_REQUIRED,
  DECLINED,
  DECLINED_CANCELLED,
  DELIVERY_MISTAKE,
  PROCESSING,
  RETURNED,
  RETURNING,
  SHIPPED,
  SUBMITTED
} from 'constants/orderStatuses';
import {
  AVAILABLE_FOR_PICKUP,
  DELAYED,
  DELIVERED,
  DELIVERY_ATTEMPTED,
  IN_TRANSIT,
  OUT_FOR_DELIVERY,
  RETURNED as RETURNED_SHIPPING,
  RETURNING as RETURNING_SHIPPING,
  SHIPPING_SOON,
  UNDELIVERABLE
} from 'constants/shippingStatuses';
import { DROPOFF_PENDING, PICKUP_SCHEDULED, PROCESSING_RETURN, RETURNED_TO_ZAPPOS, RETURNING_TO_ZAPPOS } from 'constants/returnTrackingStatuses';
import Tooltip from 'components/common/Tooltip';
import useMartyContext from 'hooks/useMartyContext';
import { track } from 'apis/amethyst';
import { evOrderStatusHover } from 'events/account';
import { RETURN_TRANSPORT_PLAN_NAME } from 'constants/returnLabelOptions';
import { getScreenSize } from 'helpers/HtmlHelpers';
import { useWindowEvent } from 'hooks/useEvent';
import { cn } from 'helpers/classnames';

import css from 'styles/components/account/orderStatus.scss';
// eslint-disable-next-line css-modules/no-unused-class
import tooltipCSS from 'styles/components/common/tooltip.scss';

const fallbackStatuses = {
  submitted: { title: 'Submitted' },
  declined: { title: 'Customer Action Required' },
  processing: { title: 'Processing' },
  cancelled: { title: 'Cancelled' },
  completed: { title: 'Completed' },
  shipped: { title: 'Shipped' },
  deliveryMistake: { title: 'Delivery Mistake' },
  delayed: { title: 'Delayed' },
  declinedCancelled: { title: 'Canceled - Payment Failure' },
  customerActionRequired: { title: 'Customer Action Required' }
};

const fallbackLegacyReturnStatuses = {
  returning: { title: 'Return in Progress' },
  returned: { title: 'Returned' }
};

const fallbackShippingStatuses = {
  shippingSoon: { title: 'Shipping soon' },
  delivered: { title: 'Delivered' },
  inTransit: { title: 'In transit' },
  outForDelivery: { title: 'Out for delivery' },
  deliveryAttempted: { title: 'Delivery attempted' },
  availableForPickup: { title: 'Available for pickup' },
  returnedShipping: { title: 'Undeliverable' },
  returningShipping: { title: 'Undeliverable' },
  delayed: { title: 'Delayed' },
  undeliverable: { title: 'Undeliverable' }
};

const fallbackReturnTrackingStatuses = {
  returnTrackingProcessingReturn: { title: 'Processing Return' },
  returnTrackingReturnScheduledPickup: {
    title: 'Return Scheduled For Pick-Up'
  },
  returnTrackingReturnViaUPSDropoff: { title: 'Return Via UPS Drop-Off' },
  returnTrackingReturningZappos: { title: 'Returning To Zappos' },
  returnTrackingReturnedZappos: { title: 'Refund Issued' }
};

export const makeShippingStatuses = (symphonyStatuses = {}) => {
  const {
    shippingSoon,
    delivered,
    inTransit,
    outForDelivery,
    deliveryAttempted,
    availableForPickup,
    returnedShipping,
    returningShipping,
    delayed,
    undeliverable
  } = symphonyStatuses;

  return {
    [SHIPPING_SOON]: shippingSoon || fallbackShippingStatuses.shippingSoon,
    [DELIVERED]: delivered || fallbackShippingStatuses.delivered,
    [IN_TRANSIT]: inTransit || fallbackShippingStatuses.inTransit,
    [OUT_FOR_DELIVERY]: outForDelivery || fallbackShippingStatuses.outForDelivery,
    [DELIVERY_ATTEMPTED]: deliveryAttempted || fallbackShippingStatuses.deliveryAttempted,
    [AVAILABLE_FOR_PICKUP]: availableForPickup || fallbackShippingStatuses.availableForPickup,
    [RETURNED_SHIPPING]: returnedShipping || fallbackShippingStatuses.returnedShipping,
    [RETURNING_SHIPPING]: returningShipping || fallbackShippingStatuses.returningShipping,
    [DELAYED]: delayed || fallbackShippingStatuses.delayed,
    [UNDELIVERABLE]: undeliverable || fallbackShippingStatuses.undeliverable
  };
};

// helper method that reconciles what we get back from Symphony, what we get back from the API, and what we should fallback to.
export const makeOrderStatuses = (symphonyStatuses = {}) => {
  const { submitted, declined, delayed, processing, cancelled, completed, shipped, deliveryMistake, declinedCancelled, customerActionRequired } =
    symphonyStatuses;

  return {
    [SUBMITTED]: submitted || fallbackStatuses.submitted,
    [DECLINED]: declined || fallbackStatuses.declined,
    [PROCESSING]: processing || fallbackStatuses.processing,
    [CANCELLED]: cancelled || fallbackStatuses.cancelled,
    [COMPLETED]: completed || fallbackStatuses.completed,
    [SHIPPED]: shipped || fallbackStatuses.shipped,
    [DELIVERY_MISTAKE]: deliveryMistake || fallbackStatuses.deliveryMistake,
    [DELAYED]: delayed || fallbackStatuses.delayed,
    [DECLINED_CANCELLED]: declinedCancelled || fallbackStatuses.declinedCancelled,
    [CUSTOMER_ACTION_REQUIRED]: customerActionRequired || fallbackStatuses.customerActionRequired
  };
};

export const makeLegacyOrderReturnStatuses = (symphonyStatuses = {}) => {
  const { returning, returned } = symphonyStatuses;

  return {
    [RETURNING]: returning || fallbackLegacyReturnStatuses.returning,
    [RETURNED]: returned || fallbackLegacyReturnStatuses.returned
  };
};

export const getReturnTrackingDropOffPendingContent = (symphonyStatuses = {}, planName) => {
  const {
    returnTrackingReturnViaUPSDropoff,
    returnTrackingReturnViaUPSDropoffLabelless,
    returnTrackingReturnViaUPSPackageless,
    returnTrackingReturnViaWFMPackageless
  } = symphonyStatuses;

  const isLabellessReturn = planName === RETURN_TRANSPORT_PLAN_NAME.LabelLess_UPSZapposDropOff;
  const isUPSPackagelessReturn = planName === RETURN_TRANSPORT_PLAN_NAME.PackageLess_UPSZapposDropOff;
  const isWFMPackagelessReturn = planName === RETURN_TRANSPORT_PLAN_NAME.PackageLess_WFMZapposDropOff;

  if (isLabellessReturn) {
    return returnTrackingReturnViaUPSDropoffLabelless;
  }

  if (isUPSPackagelessReturn) {
    return returnTrackingReturnViaUPSPackageless;
  }

  if (isWFMPackagelessReturn) {
    return returnTrackingReturnViaWFMPackageless;
  }

  return returnTrackingReturnViaUPSDropoff || fallbackReturnTrackingStatuses.returnTrackingReturnViaUPSDropoff;
};

// TODO: should remove this function when backend is ready to handle WFM return status tracking. https://sim.amazon.com/issues/P76166925
const getWFMPackagelessStatusForCustomerActionRequired = (planName, dropoffPending, customerActionRequired) =>
  planName === RETURN_TRANSPORT_PLAN_NAME.PackageLess_WFMZapposDropOff
    ? dropoffPending
    : customerActionRequired || fallbackStatuses.customerActionRequired;

export const makeReturnTrackingStatuses = (symphonyStatuses = {}, planName) => {
  const {
    returnTrackingProcessingReturn,
    returnTrackingReturnScheduledPickup,
    returnTrackingReturnedZappos,
    returnTrackingReturningZappos,
    customerActionRequired
  } = symphonyStatuses;
  const dropoffPending = getReturnTrackingDropOffPendingContent(symphonyStatuses, planName, customerActionRequired);

  return {
    [DROPOFF_PENDING]: dropoffPending,
    [PICKUP_SCHEDULED]: returnTrackingReturnScheduledPickup || fallbackReturnTrackingStatuses.returnTrackingReturnScheduledPickup,
    [PROCESSING_RETURN]: returnTrackingProcessingReturn || fallbackReturnTrackingStatuses.returnTrackingProcessingReturn,
    [RETURNED_TO_ZAPPOS]: returnTrackingReturnedZappos || fallbackReturnTrackingStatuses.returnTrackingReturnedZappos,
    [RETURNING_TO_ZAPPOS]: returnTrackingReturningZappos || fallbackReturnTrackingStatuses.returnTrackingReturningZappos,
    [CUSTOMER_ACTION_REQUIRED]: getWFMPackagelessStatusForCustomerActionRequired(planName, dropoffPending)
  };
};
/**
 *
 * @param orderStatuses - symphony status
 * @param planName - plan name
 * @param status - lineItem tracking status
 * @returns {object|null}
 */
export const getStatusMap = (orderStatuses, planName, status) => {
  const statuses = makeOrderStatuses(orderStatuses);
  const shippingStatuses = makeShippingStatuses(orderStatuses);
  const legacyOrderReturnStatuses = makeLegacyOrderReturnStatuses(orderStatuses);
  const returnTrackingStatuses = makeReturnTrackingStatuses(orderStatuses, planName);
  const isWFMPackagelessReturn = planName === RETURN_TRANSPORT_PLAN_NAME.PackageLess_WFMZapposDropOff;
  let statusMap = {};

  // TODO: only do this first if conditional if you're in the shipping dates test.
  if (shippingStatuses[status]) {
    // check if there is a corresponding shipment-level status
    statusMap = shippingStatuses[status];
  } else if (statuses[status] && !isWFMPackagelessReturn) {
    // TODO: should remove this check for isWFMPackagelessReturn when backend is ready to handle WFM return status tracking. https://sim.amazon.com/issues/P76166925
    // check if there is a corresponding lineitem-level status
    statusMap = statuses[status];
  } else if (legacyOrderReturnStatuses[status]) {
    statusMap = legacyOrderReturnStatuses[status];
  } else if (returnTrackingStatuses[status]) {
    statusMap = returnTrackingStatuses[status];
  } else {
    return null; // exit early if no status that can be mapped
  }
  return statusMap;
};
export const OrderStatus = ({ status, orderStatuses, setToolTipVisibilityforMobile, planName }) => {
  const { testId } = useMartyContext();
  const isWFMPackagelessReturn = planName === RETURN_TRANSPORT_PLAN_NAME.PackageLess_WFMZapposDropOff;

  const [firedTracking, setFiredTracking] = useState(false);
  const [tooltipDirection, setTooltipDirection] = useState('right');

  const handleOrderStatusHoverTrack = e => {
    const contentBox = document.getElementsByClassName(tooltipCSS.content)[0]; // active overlay would cause this to fire, so checking for content before setting the value to true
    if (setToolTipVisibilityforMobile && !contentBox) {
      setToolTipVisibilityforMobile(true);
    }

    if (!firedTracking) {
      setFiredTracking(true);
      const { orderStatus } = e.currentTarget.dataset;
      track(() => [evOrderStatusHover, { orderStatus }]);
    }
  };

  const handleBlur = () => {
    if (setToolTipVisibilityforMobile) {
      setToolTipVisibilityforMobile(false);
    }
  };

  useWindowEvent('resize', () => {
    if (getScreenSize() === 'desktop') {
      setTooltipDirection('left');
    } else {
      setTooltipDirection('right');
    }
  });

  const statusMap = getStatusMap(orderStatuses, planName, status);
  if (!statusMap) return null;
  const { title, description } = statusMap;

  // `statusTooltipContent` will be undefined unless a title AND description are defined
  const statusTooltipContent = title && description && (
    <div>
      <h4>{title}</h4>
      <div>{description}</div>
    </div>
  );

  const statusElement = (
    <span className={getItemStatusClassName(status, isWFMPackagelessReturn)} data-test-id={testId('orderStatusValue')}>
      {title}
    </span>
  );

  return (
    <span
      data-test-id={testId('orderStatus')}
      data-order-status={status}
      onMouseOver={handleOrderStatusHoverTrack}
      onFocus={handleOrderStatusHoverTrack}
      onBlur={handleBlur}
      onMouseOut={handleBlur}
      className={css.orderStatusWrapper}
    >
      {statusTooltipContent ? ( // render tooltip or just the status if no description
        <Tooltip
          tooltipId={status}
          wrapperClassName={css.tooltipWrapper}
          contentClassName={cn(css.tooltipContent, 'zappos:body-s')}
          tooltipClassName={css.tooltip}
          direction={tooltipDirection}
          content={statusTooltipContent}
        >
          {statusElement}
        </Tooltip>
      ) : (
        statusElement
      )}
    </span>
  );
};

const mapStateToProps = state => {
  const { content } = state.account || {};
  const orderStatuses = content?.['order-status-descriptions'];

  return {
    orderStatuses: orderStatuses
  };
};

OrderStatus.displayName = 'OrderStatus';
const ConnectedOrderStatus = connect(mapStateToProps)(OrderStatus);
export default withErrorBoundary(OrderStatus.displayName, ConnectedOrderStatus);
